import { Controller } from '@hotwired/stimulus'

const BUMP_COUNTDOWN_SECONDS = 5

function csrfToken() {
  return document.querySelector('meta[name=csrf-token]')?.content
}

async function patchUrl(url) {
  const resp = await fetch(url, {
    method: 'PATCH',
    headers: { 'X-CSRF-Token': csrfToken() },
  })
  if (!resp.ok) throw new Error(`PATCH ${url} failed: ${resp.status}`)
}

export default class extends Controller {
  static targets = ['bumpBtn', 'bumpLabel', 'bumpProgress', 'rushBtn']
  static values = {
    completeUrl: String,
    rushUrl: String,
    unrushUrl: String,
  }

  connect() {
    this.bumping = false
    this.bumpTimer = null
  }

  disconnect() {
    if (this.bumpTimer) clearInterval(this.bumpTimer)
  }

  // ── Individual item check (toggles complete ↔ uncomplete) ─────────────

  async checkItem(event) {
    const checkEl = event.currentTarget
    const row = checkEl.closest('.kds-chit-item')
    const nameEl = row.querySelector('.kds-item-name')
    const isDone = checkEl.classList.contains('kds-item-check--done')
    const url = isDone
      ? checkEl.dataset.kdsChitUncompleteUrlParam
      : checkEl.dataset.kdsChitCompleteUrlParam

    // Optimistic toggle
    checkEl.classList.toggle('kds-item-check--done', !isDone)
    nameEl?.classList.toggle('kds-item-name--done', !isDone)

    try {
      await patchUrl(url)
      this.dispatch('item-checked', { bubbles: true })
    } catch {
      // Revert on failure
      checkEl.classList.toggle('kds-item-check--done', isDone)
      nameEl?.classList.toggle('kds-item-name--done', isDone)
      return
    }

    if (!isDone) this.checkAllItemsDone()
  }

  checkAllItemsDone() {
    const all = this.element.querySelectorAll('.kds-item-check')
    const done = this.element.querySelectorAll('.kds-item-check--done')
    if (all.length > 0 && all.length === done.length) {
      setTimeout(() => this.startBump(), 300)
    }
  }

  // ── Bump countdown ────────────────────────────────────────────────────

  toggleBump() {
    this.bumping ? this.cancelBump() : this.startBump()
  }

  startBump() {
    if (this.bumping) return
    this.bumping = true
    this.element.classList.add('kds-station-chit--counting-down')

    let remaining = BUMP_COUNTDOWN_SECONDS
    this.bumpLabelTarget.textContent = `Bumping in ${remaining}s — tap to cancel`
    this.bumpProgressTarget.style.transition = `width ${BUMP_COUNTDOWN_SECONDS}s linear`
    this.bumpProgressTarget.style.width = '100%'

    this.bumpTimer = setInterval(() => {
      remaining -= 1
      if (remaining <= 0) {
        clearInterval(this.bumpTimer)
        this.bumpTimer = null
        this.executeComplete()
      } else {
        this.bumpLabelTarget.textContent = `Bumping in ${remaining}s — tap to cancel`
      }
    }, 1000)
  }

  cancelBump() {
    if (!this.bumping) return
    if (this.bumpTimer) {
      clearInterval(this.bumpTimer); this.bumpTimer = null
    }
    this.bumping = false
    this.element.classList.remove('kds-station-chit--counting-down')
    this.bumpProgressTarget.style.transition = 'none'
    this.bumpProgressTarget.style.width = '0%'
    this.bumpLabelTarget.textContent = ''
    this.dispatch('bump-cancelled', { bubbles: true })
  }

  async executeComplete() {
    this.element.classList.remove('kds-station-chit--counting-down')
    this.element.classList.add('kds-station-chit--bumping')

    try {
      await patchUrl(this.completeUrlValue)
    } catch {
      this.element.classList.remove('kds-station-chit--bumping')
      this.cancelBump()
      return
    }

    this.dispatch('bumped', { bubbles: true })
    this.element.remove()
  }

  // ── Rush / Unrush (server-persisted) ─────────────────────────────────

  async toggleRush() {
    const isRushed = this.element.classList.contains('kds-station-chit--rushed')
    const url = isRushed ? this.unrushUrlValue : this.rushUrlValue

    // Optimistic toggle
    this.element.classList.toggle('kds-station-chit--rushed', !isRushed)
    this.rushBtnTarget.classList.toggle('kds-rush-btn--active', !isRushed)

    try {
      await patchUrl(url)
      this.dispatch('rushed', { bubbles: true })
    } catch {
      // Revert on failure
      this.element.classList.toggle('kds-station-chit--rushed', isRushed)
      this.rushBtnTarget.classList.toggle('kds-rush-btn--active', isRushed)
    }
  }
}
